// Revisado: 2025-12-18 [I18n Scan]
import React, { useState } from 'react';
import { createPortal } from 'react-dom'; // NEW: For Modal
import SupportList from './SupportList';
import SupportHints from './SupportHints';
import { useTranslation } from 'react-i18next';

const SupportSelector = ({ slots, setSlots, activeSkills, onAddSkill, allSkills }) => {
    const { t } = useTranslation();
    // 6 Slots State
    // Each slot: { support: object | null, capLevel: number (0-4) }
    // State is now lifted to App.js via props
    // const [slots, setSlots] = useState(Array(6).fill({ support: null, capLevel: 0 }));
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentSlotIndex, setCurrentSlotIndex] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false); // FIXED: Re-added missing state
    const [readyToRenderHints, setReadyToRenderHints] = useState(false); // NEW: Defer heavy hints

    // NEW: Defer hints rendering to avoid blocking the initial cascade
    React.useEffect(() => {
        const timer = setTimeout(() => {
            setReadyToRenderHints(true);
        }, 800); // Wait 800ms (after cascade finishes)
        return () => clearTimeout(timer);
    }, []);

    const handleSlotClick = (index) => {
        // Only open modal if clicking the card area, not the caps
        setCurrentSlotIndex(index);
        setIsModalOpen(true);
    };

    // ... (rest of handlers remain same) ...

    const handleSupportSelect = (support) => {
        const newSlots = [...slots];
        newSlots[currentSlotIndex] = {
            support: support,
            capLevel: 0 // Default to 0 on new selection
        };
        setSlots(newSlots);
        setIsModalOpen(false);
    };

    const handleRightClick = (e, index) => {
        e.preventDefault();
        const newSlots = [...slots];
        newSlots[index] = { support: null, capLevel: 0 };
        setSlots(newSlots);
    };

    // Logic for Cap Selection
    // index: Slot Index
    // capIndex: Which crystal was clicked (0, 1, 2, 3) representing Cap 1, 2, 3, 4
    const handleCapClick = (e, slotIndex, capIndex) => {
        e.stopPropagation(); // Prevent opening modal
        const currentSlot = slots[slotIndex];
        if (!currentSlot.support) return; // Ignore if empty slot

        const clickedLevel = capIndex + 1; // 0th crystal -> Level 1
        let newLevel = clickedLevel;

        // Logic:
        // - Should turn on in order (clicking 3 turns on 1, 2, 3) -> inherent in setting level
        // - If > 1 are on (e.g. 3) and click 1: All turn off except 1 -> Set level 1
        // - If 1 is off, click 1 -> Turn on (Level 1)
        // - If only 1 is on, click 1 -> Turn off (Level 0)

        if (currentSlot.capLevel === 1 && clickedLevel === 1) {
            newLevel = 0; // Toggle off if only 1 was active
        }

        const newSlots = [...slots];
        newSlots[slotIndex] = { ...currentSlot, capLevel: newLevel };
        setSlots(newSlots);
    };

    const getTypeIconPath = (supportType) => {
        if (!supportType) return '';
        const type = supportType.charAt(0).toUpperCase() + supportType.slice(1).toLowerCase();

        if (['Speed', 'Stamina', 'Power', 'Guts', 'Wit'].includes(type)) {
            return `/images/Icon/Stats_icons/Stat_Type_${type}.png`;
        } else if (['Pal', 'Group'].includes(type)) {
            return `/images/Icon/Support_Icons/Support_Type_${type}.png`;
        }
        return '';
    };

    // Shared Colors with SupportCards.js / SupportHints.js
    const SLOT_COLORS = [
        { bg: 'bg-red-500', shadow: 'shadow-[0_0_8px_rgba(239,68,68,0.8)]' },
        { bg: 'bg-blue-500', shadow: 'shadow-[0_0_8px_rgba(59,130,246,0.8)]' },
        { bg: 'bg-emerald-500', shadow: 'shadow-[0_0_8px_rgba(16,185,129,0.8)]' },
        { bg: 'bg-orange-500', shadow: 'shadow-[0_0_8px_rgba(249,115,22,0.8)]' },
        { bg: 'bg-violet-500', shadow: 'shadow-[0_0_8px_rgba(139,92,246,0.8)]' },
        { bg: 'bg-cyan-500', shadow: 'shadow-[0_0_8px_rgba(6,182,212,0.8)]' }
    ];

    return (
        <div className="w-full bg-white rounded-xl shadow-sm mb-6 overflow-hidden transition-all duration-300">
            {/* Header / Toggle Bar */}
            <div
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
            >
                <div>
                    <h2 className="text-xl font-bold text-gray-800">{t('SupportSelector.title')}</h2>
                    <p className="text-sm text-gray-500">
                        {t('SupportSelector.count_label', { count: slots.filter(s => s.support !== null).length })}
                    </p>
                </div>
                <svg
                    className={`w-6 h-6 text-gray-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </div>

            {isExpanded && <div className="h-px bg-gray-200 mx-4"></div>}

            <div className={`transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                <div className="p-4">
                    <div className="md:hidden mb-2 text-xs text-center text-gray-400 animate-pulse">
                        {t('SupportSelector.swipe_hint')}
                    </div>

                    <div className="
                        w-full 
                        flex 
                        gap-4 
                        overflow-x-auto 
                        snap-x 
                        snap-mandatory 
                        pb-4
                        px-2
                        md:flex-wrap 
                        md:justify-center 
                        md:overflow-visible 
                        md:px-0
                        scrollbar-hide
                    ">
                        {slots.map((slot, index) => {
                            const support = slot.support;
                            const cardImagePath = support
                                ? `/images/Support/Card/Support_Card_${support.support_id}_Card.png`
                                : null;
                            const typeIconPath = support ? getTypeIconPath(support.Support_Type) : null;
                            const slotColor = SLOT_COLORS[index % SLOT_COLORS.length];

                            return (
                                <div key={index} className="flex flex-col items-center shrink-0 snap-center">

                                    {/* LED Indicator */}
                                    <div className={`w-12 h-1.5 rounded-full mb-1 transition-all duration-500 
                                            ${support ? `${slotColor.bg} ${slotColor.shadow}` : 'bg-gray-200'} `}
                                    ></div>

                                    {/* CARD SLOT */}
                                    <div
                                        onClick={() => handleSlotClick(index)}
                                        onContextMenu={(e) => handleRightClick(e, index)}
                                        className="relative cursor-pointer transition-transform hover:scale-105 group mb-2"
                                        style={{
                                            width: '120px',
                                            height: '160px',
                                            backgroundColor: 'rgb(247, 248, 247)',
                                            border: '2px solid rgb(217, 216, 217)',
                                            borderRadius: '12px',
                                            overflow: 'hidden',
                                            boxShadow: support ? 'none' : 'inset 0px 12px 12px -6px rgba(0,0,0,0.08), inset 6px 0px 8px -4px rgba(0,0,0,0.05), inset -6px 0px 8px -4px rgba(0,0,0,0.05)'
                                        }}
                                    >
                                        {support ? (
                                            <>
                                                <img
                                                    src={cardImagePath}
                                                    alt={support.char_name}
                                                    className="w-full h-full object-cover"
                                                    loading="lazy"
                                                    decoding="async"
                                                    onError={(e) => { e.target.src = '/images/placeholder.png'; }}
                                                />
                                                {typeIconPath && (
                                                    <div className="absolute bottom-1 right-1 bg-black/30 rounded-full p-0.5 backdrop-blur-sm">
                                                        <img src={typeIconPath} className="w-6 h-6 object-contain" alt={support.Support_Type} />
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <svg width="40" height="40" viewBox="0 0 50 50" className="select-none transition-transform group-hover:scale-110">
                                                    <defs>
                                                        <linearGradient id="plusGradient" x1="0%" y1="100%" x2="0%" y2="0%">
                                                            <stop offset="0%" stopColor="rgb(177, 225, 12)" />
                                                            <stop offset="100%" stopColor="rgb(129, 211, 33)" />
                                                        </linearGradient>
                                                    </defs>
                                                    <rect x="20" y="0" width="10" height="50" rx="3" fill="url(#plusGradient)" />
                                                    <rect x="0" y="20" width="50" height="10" rx="3" fill="url(#plusGradient)" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>

                                    {/* LIMIT BREAK CRYSTALS (CAPS) */}
                                    {support ? (
                                        <div className="flex gap-1 justify-center h-7 items-center bg-gray-50 rounded-full px-2 py-0.5 border border-gray-100 shadow-sm">
                                            {[0, 1, 2, 3].map((capIndex) => {
                                                // capIndex 0 = Cap 1
                                                // capIndex 1 = Cap 2 ...
                                                const isActive = (capIndex + 1) <= slot.capLevel;

                                                return (
                                                    <img
                                                        key={capIndex}
                                                        src="/images/Icon/Resources/CrystalCap.png"
                                                        alt={`Cap ${capIndex + 1}`}
                                                        onClick={(e) => handleCapClick(e, index, capIndex)}
                                                        className={`w-5 h-auto cursor-pointer transition-all hover:scale-110 ${isActive ? '' : 'grayscale opacity-40'}`}
                                                    />
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        // Placeholder height to keep alignment
                                        <div className="h-7 w-full"></div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <p className="mt-2 text-xs text-gray-400 text-center">
                        {t('SupportSelector.card_instructions')}
                    </p>

                    {/* Support Hints Integration - NOW DEFERRED */}
                    {readyToRenderHints && slots.some(s => s.support) && (
                        <div className="mt-4 border-t pt-4 animate-fade-in">
                            <SupportHints
                                slots={slots}
                                activeSkills={activeSkills}
                                onAddSkill={onAddSkill}
                                allSkills={allSkills}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Modal de Selección - VIA PORTAL */}
            {isModalOpen && createPortal(
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white rounded-xl shadow-2xl p-6 max-w-5xl w-full max-h-[90vh] flex flex-col relative animate-in fade-in zoom-in duration-200" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold text-gray-800">{t('SupportSelector.modal_title')}</h3>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            <SupportList onSelect={handleSupportSelect} />
                        </div>
                    </div>
                </div>,
                document.body // Pinta en el body
            )}

            <style>{`
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>
        </div>
    );
};

export default SupportSelector;
